import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  PencilIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { AdminUser } from '@/shared/db';

interface AdminTableProps {
  admins: AdminUser[];
  onEdit: (admin: AdminUser) => void;
  onDeactivate: (adminId: string) => void;
  currentUserId?: string;
}

export const AdminTable: React.FC<AdminTableProps> = ({
  admins,
  onEdit,
  onDeactivate,
  currentUserId,
}) => {
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'admin':
        return 'bg-tiber-100 text-tiber-800 border-tiber-200';
      case 'moderator':
        return 'bg-atlantis-100 text-green-800 border-atlantis-200';
      default:
        return 'bg-tiber-100 text-tiber-800 border-tiber-200';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-romance-200">
        <thead className="bg-romance-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.settings.admins.table.email')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.settings.admins.table.name')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.settings.admins.table.role')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.settings.admins.table.created')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.settings.admins.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-romance-200">
          {admins.map((admin) => (
            <motion.tr
              key={admin.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="hover:bg-romance-50 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-juridico">
                  {admin.email || 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-juridico">
                  {admin.full_name || 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleColor(
                    admin.role
                  )}`}
                >
                  {t(`admin.roles.${admin.role}`)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico/60">
                {formatDate(admin.created_at)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onEdit(admin)}
                    className="text-tiber-500 hover:text-tiber-500/80 transition-colors"
                    title={t('admin.admins.actions.edit')}
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  {admin.id !== currentUserId && (
                    <button
                      onClick={() => onDeactivate(admin.id)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title={t('admin.admins.actions.deactivate')}
                    >
                      <XCircleIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

